<template>
  <div class="supplier-groups-page space-y-5 stagger-children">
    <!-- Error Banner -->
    <AlertBanner v-if="error" type="error" :message="error" @dismiss="error = null">
      <template #action>
        <button
          @click="loadGroups"
          class="text-sm font-semibold text-red-600 dark:text-red-400 hover:underline"
        >
          Retry
        </button>
      </template>
    </AlertBanner>

    <!-- Page Header -->
    <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
      <div>
        <h1 class="text-2xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">
          Supplier Groups
        </h1>
        <p class="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">
          Create and manage supplier groups for analytics and reporting
        </p>
      </div>
      <button
        @click="openCreateModal"
        class="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-semibold bg-primary-700 text-white hover:bg-primary-800 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-neutral-900 self-start lg:self-auto"
      >
        <span class="material-icons text-[18px]" aria-hidden="true">add</span>
        New Group
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      <div
        v-for="n in 3"
        :key="n"
        class="bg-white dark:bg-neutral-800 rounded-xl shadow-soft border border-neutral-200 dark:border-neutral-700 p-5 animate-pulse"
      >
        <div class="h-5 bg-neutral-200 dark:bg-neutral-700 rounded w-2/3 mb-3"></div>
        <div class="h-4 bg-neutral-100 dark:bg-neutral-700 rounded w-1/2 mb-4"></div>
        <div class="flex gap-2 mb-4">
          <div class="h-6 bg-neutral-100 dark:bg-neutral-700 rounded-full w-20"></div>
          <div class="h-6 bg-neutral-100 dark:bg-neutral-700 rounded-full w-16"></div>
        </div>
        <div class="h-px bg-neutral-100 dark:bg-neutral-700 mb-3"></div>
        <div class="flex gap-4">
          <div class="h-4 bg-neutral-100 dark:bg-neutral-700 rounded w-24"></div>
          <div class="h-4 bg-neutral-100 dark:bg-neutral-700 rounded w-12"></div>
          <div class="h-4 bg-neutral-100 dark:bg-neutral-700 rounded w-14"></div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div
      v-else-if="!groups.length"
      class="bg-white dark:bg-neutral-800 rounded-xl shadow-soft border border-neutral-200 dark:border-neutral-700 p-12 text-center"
    >
      <span class="material-icons text-5xl text-neutral-300 dark:text-neutral-600" aria-hidden="true">group_work</span>
      <h3 class="mt-3 text-base font-semibold text-neutral-700 dark:text-neutral-300">No supplier groups yet</h3>
      <p class="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
        Create a group to start tracking and comparing suppliers together.
      </p>
      <button
        @click="openCreateModal"
        class="mt-5 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-semibold bg-primary-700 text-white hover:bg-primary-800 transition-all duration-200"
      >
        <span class="material-icons text-[18px]" aria-hidden="true">add</span>
        New Group
      </button>
    </div>

    <!-- Groups Grid -->
    <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      <div
        v-for="group in groups"
        :key="group.id"
        class="bg-white dark:bg-neutral-800 rounded-xl shadow-soft border border-neutral-200 dark:border-neutral-700 border-l-4 border-l-primary-600 p-5 flex flex-col gap-3 hover:shadow-md transition-shadow duration-200"
      >
        <!-- Group Name & Subtitle -->
        <div>
          <h3 class="text-base font-bold text-neutral-900 dark:text-neutral-100 leading-snug">
            {{ group.name }}
          </h3>
          <p class="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
            {{ group.supplierIds?.length || 0 }} supplier{{ (group.supplierIds?.length || 0) !== 1 ? 's' : '' }}
            <template v-if="locationCount(group) > 0">
              &middot; {{ locationCount(group) }} location{{ locationCount(group) !== 1 ? 's' : '' }}
            </template>
          </p>
        </div>

        <!-- Description -->
        <p v-if="group.description" class="text-sm text-neutral-600 dark:text-neutral-400 -mt-1 line-clamp-2">
          {{ group.description }}
        </p>

        <!-- Member Supplier Tags -->
        <div class="flex flex-wrap gap-1.5">
          <template v-if="group.supplierIds && group.supplierIds.length">
            <span
              v-for="supplierId in group.supplierIds.slice(0, 2)"
              :key="supplierId"
              class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 border border-primary-100 dark:border-primary-800/30"
            >
              {{ getSupplierLabel(supplierId) }}
            </span>
            <span
              v-if="group.supplierIds.length > 2"
              class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-400"
            >
              +{{ group.supplierIds.length - 2 }} more
            </span>
          </template>
          <span v-else class="text-xs text-neutral-400 dark:text-neutral-500 italic">
            No suppliers
          </span>
        </div>

        <!-- Divider -->
        <div class="border-t border-neutral-100 dark:border-neutral-700 mt-auto pt-3 flex items-center gap-4">
          <button
            @click="viewAnalytics(group)"
            class="text-xs font-semibold text-primary-700 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 transition-colors inline-flex items-center gap-1"
          >
            <span class="material-icons text-[14px]" aria-hidden="true">bar_chart</span>
            View Analytics
          </button>
          <button
            @click="openEditModal(group)"
            class="text-xs font-semibold text-neutral-600 dark:text-neutral-400 hover:text-neutral-800 dark:hover:text-neutral-200 transition-colors inline-flex items-center gap-1"
          >
            <span class="material-icons text-[14px]" aria-hidden="true">edit</span>
            Edit
          </button>
          <button
            @click="confirmDelete(group)"
            class="text-xs font-semibold text-red-700 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 transition-colors inline-flex items-center gap-1 ml-auto"
          >
            <span class="material-icons text-[14px]" aria-hidden="true">delete_outline</span>
            Delete
          </button>
        </div>
      </div>
    </div>

    <!-- Create / Edit Modal -->
    <Modal
      :show="showModal"
      :title="isEditing ? 'Edit Group' : 'Create Supplier Group'"
      size="md"
      :closable="true"
      @close="closeModal"
    >
      <form @submit.prevent="saveGroup" class="space-y-4">
        <!-- Save error: rendered inside the modal — the page-level banner is
             hidden behind the backdrop while this modal is open -->
        <AlertBanner
          v-if="modalError"
          type="error"
          :message="modalError"
          :dismissible="false"
        />

        <!-- Group Name -->
        <div>
          <label for="group-name" class="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-1.5">
            Group Name <span class="text-red-500" aria-hidden="true">*</span>
          </label>
          <input
            id="group-name"
            v-model="form.name"
            type="text"
            required
            placeholder="e.g. UAE Premium Suppliers"
            class="w-full px-4 py-2.5 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 transition-all duration-200 text-sm"
          />
        </div>

        <!-- Description -->
        <div>
          <label for="group-description" class="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-1.5">
            Description
          </label>
          <input
            id="group-description"
            v-model="form.description"
            type="text"
            placeholder="Optional description"
            class="w-full px-4 py-2.5 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 transition-all duration-200 text-sm"
          />
        </div>

        <!-- Select Suppliers -->
        <div>
          <div class="flex items-center justify-between mb-1.5">
            <label for="group-suppliers" class="block text-sm font-semibold text-neutral-700 dark:text-neutral-300">
              Select Suppliers
            </label>
            <div class="flex gap-2">
              <button
                type="button"
                @click="form.supplierIds = supplierOptions.map(o => o.value)"
                class="text-xs font-medium text-primary-700 dark:text-primary-400 hover:underline"
              >Select All</button>
              <button
                type="button"
                @click="form.supplierIds = []"
                class="text-xs font-medium text-neutral-500 dark:text-neutral-400 hover:underline"
              >Clear</button>
            </div>
          </div>
          <v-select
            v-model="form.supplierIds"
            input-id="group-suppliers"
            :options="supplierOptions"
            :multiple="true"
            :close-on-select="false"
            label="label"
            :reduce="opt => opt.value"
            placeholder="Search suppliers..."
            class="v-select-custom"
          />
          <p class="text-xs text-neutral-400 dark:text-neutral-500 mt-1" v-if="form.supplierIds.length">
            {{ form.supplierIds.length }} supplier{{ form.supplierIds.length !== 1 ? 's' : '' }} selected
          </p>
        </div>
      </form>

      <template #footer>
        <div class="flex items-center justify-end gap-2.5">
          <button
            @click="closeModal"
            type="button"
            class="px-4 py-2.5 rounded-lg text-sm font-semibold text-neutral-700 dark:text-neutral-300 bg-neutral-100 dark:bg-neutral-700 hover:bg-neutral-200 dark:hover:bg-neutral-600 transition-all duration-200"
          >
            Cancel
          </button>
          <button
            @click="saveGroup"
            :disabled="saving || !form.name.trim()"
            class="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-semibold bg-primary-700 text-white hover:bg-primary-800 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-neutral-900 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg v-if="saving" class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {{ saving ? 'Saving...' : 'Save' }}
          </button>
        </div>
      </template>
    </Modal>

    <!-- Delete Confirmation -->
    <ConfirmDialog
      :show="deleteState.show"
      title="Delete group"
      :message="`Delete group '${deleteState.group?.name}'? This cannot be undone.`"
      confirm-label="Delete"
      :danger="true"
      :loading="deleteState.loading"
      @confirm="doDelete"
      @cancel="deleteState.show = false"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted, watchEffect } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore'
import { db } from '@/firebase/config'
import { useSupplierGroups } from '@/composables/useSupplierGroups'
import useToast from '@/composables/useToast'
import Modal from '@/components/ui/Modal.vue'
import ConfirmDialog from '@/components/ui/ConfirmDialog.vue'
import AlertBanner from '@/components/ui/AlertBanner.vue'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'

export default {
  name: 'SupplierGroups',
  components: {
    Modal,
    ConfirmDialog,
    AlertBanner,
    vSelect,
  },
  setup() {
    const store = useStore()
    const router = useRouter()
    const { showToast } = useToast()
    const { groups, loading, error, fetchGroups, createGroup, updateGroup, deleteGroup } = useSupplierGroups()

    // Supplier list from Firestore
    const suppliers = ref([])

    const supplierOptions = computed(() =>
      suppliers.value.map(s => ({
        label: s.suppliername || s.name || `Supplier ${s.supplierid}`,
        value: String(s.supplierid),
      }))
    )

    // Fetch suppliers from Firestore (same pattern as Reports.vue)
    const fetchSuppliersFromFirestore = () => {
      const colRef = query(collection(db, 'suppliers'), orderBy('suppliername'))
      const unsub = onSnapshot(colRef, (snapshot) => {
        const result = []
        snapshot.docs.forEach((doc) => {
          const data = doc.data()
          if (String(data.siteid) === String(store.state.currentSite)) {
            result.push(data)
          }
        })
        suppliers.value = result
      })
      watchEffect((onInvalidate) => {
        onInvalidate(() => unsub())
      })
    }

    // Resolve a supplier display label by id
    const getSupplierLabel = (supplierId) => {
      const match = suppliers.value.find(
        s => String(s.supplierid) === String(supplierId)
      )
      if (match) return match.suppliername || match.name || `Supplier ${supplierId}`
      return `Supplier ${supplierId}`
    }

    // Compute location count for a group (sum of supplier locations)
    const locationCount = (group) => {
      if (!group.supplierIds || !group.supplierIds.length) return 0
      return group.supplierIds.reduce((total, sid) => {
        const match = suppliers.value.find(s => String(s.supplierid) === String(sid))
        return total + (match?.locations?.length || 0)
      }, 0)
    }

    // Modal state
    const showModal = ref(false)
    const isEditing = ref(false)
    const editingId = ref(null)
    const saving = ref(false)
    // Save errors render inside the modal — the page banner sits behind the backdrop.
    const modalError = ref('')

    const emptyForm = () => ({ name: '', description: '', supplierIds: [] })
    const form = ref(emptyForm())

    const openCreateModal = () => {
      isEditing.value = false
      editingId.value = null
      form.value = emptyForm()
      modalError.value = ''
      showModal.value = true
    }

    const openEditModal = (group) => {
      isEditing.value = true
      editingId.value = group.id
      form.value = {
        name: group.name || '',
        description: group.description || '',
        supplierIds: (group.supplierIds || []).map(String),
      }
      modalError.value = ''
      showModal.value = true
    }

    const closeModal = () => {
      showModal.value = false
      modalError.value = ''
    }

    const loadGroups = () => {
      fetchGroups(store.state.currentSite)
    }

    const saveGroup = async () => {
      if (!form.value.name.trim()) return
      saving.value = true
      modalError.value = ''
      try {
        const payload = {
          name: form.value.name.trim(),
          description: form.value.description.trim(),
          supplierIds: form.value.supplierIds,
          siteId: store.state.currentSite,
        }
        if (isEditing.value) {
          await updateGroup(editingId.value, payload)
        } else {
          await createGroup(payload)
        }
        closeModal()
        showToast('success', isEditing.value ? 'Group updated' : 'Group created')
        loadGroups()
      } catch {
        // Surface the composable's error inside the open modal.
        modalError.value = error.value || `Failed to ${isEditing.value ? 'update' : 'create'} group. Please try again.`
      } finally {
        saving.value = false
      }
    }

    const deleteState = ref({ show: false, group: null, loading: false })

    const confirmDelete = (group) => {
      deleteState.value = { show: true, group, loading: false }
    }

    const doDelete = async () => {
      if (!deleteState.value.group) return
      deleteState.value.loading = true
      try {
        await deleteGroup(deleteState.value.group.id)
        deleteState.value.show = false
        showToast('success', 'Group deleted')
        loadGroups()
      } catch {
        // Dialog stays open; the page banner is behind the overlay, so toast it.
        showToast('error', 'Failed to delete group. Please try again.')
      } finally {
        deleteState.value.loading = false
      }
    }

    const viewAnalytics = (group) => {
      router.push({ name: 'SupplierGroupAnalytics', params: { id: group.id } })
    }

    onMounted(() => {
      store.commit('setCurrentRoute', 'Supplier Groups')
      fetchSuppliersFromFirestore()
      loadGroups()
    })

    return {
      // Data
      groups,
      loading,
      error,
      suppliers,
      supplierOptions,
      // Modal
      showModal,
      isEditing,
      saving,
      modalError,
      form,
      deleteState,
      // Methods
      loadGroups,
      openCreateModal,
      openEditModal,
      closeModal,
      saveGroup,
      confirmDelete,
      doDelete,
      viewAnalytics,
      getSupplierLabel,
      locationCount,
    }
  },
}
</script>
